import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, CheckCircle, ArrowRight, Target, FileText, Users } from 'lucide-react';

interface PriseEnChargeSolutionsAffairesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PriseEnChargeSolutionsAffairesModal({ isOpen, onClose }: PriseEnChargeSolutionsAffairesModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-purple-500/30 shadow-2xl shadow-purple-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-purple-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                    <Users className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                      Prise en Charge Cybersécurité
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Service complet d'accompagnement cybersécurité - Méthodologie PRIsME
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-purple-600/20 rounded-xl p-8 border-2 border-purple-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Votre Parcours Cybersécurité avec BZ Inc.
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  La prise en charge, c'est le début de tout. Nous comprenons où vous en êtes, où vous voulez aller, 
                  et comment nous allons vous y amener. C'est notre <strong className="text-purple-400">méthodologie PRIsME</strong> appliquée à la cybersécurité.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  {
                    step: 1,
                    title: 'Évaluation Initiale',
                    description: 'Compréhension complète de votre posture cybersécurité actuelle. Infrastructure, configurations, processus, conformité. Rien n\'est laissé au hasard.',
                    icon: Target,
                    circleBg: 'bg-purple-500/20',
                    circleBorder: 'border-purple-500',
                    circleText: 'text-purple-400',
                    lineGradient: 'from-purple-500',
                    cardBg: 'from-purple-500/10 to-purple-600/10',
                    cardBorder: 'border-purple-500/30',
                    iconText: 'text-purple-400'
                  },
                  {
                    step: 2,
                    title: 'Planification Stratégique',
                    description: 'Développement d\'un plan d\'action structuré aligné sur vos objectifs business, les exigences réglementaires (Loi 25), et les standards (NIST 2.0).',
                    icon: FileText,
                    circleBg: 'bg-pink-500/20',
                    circleBorder: 'border-pink-500',
                    circleText: 'text-pink-400',
                    lineGradient: 'from-pink-500',
                    cardBg: 'from-pink-500/10 to-pink-600/10',
                    cardBorder: 'border-pink-500/30',
                    iconText: 'text-pink-400'
                  },
                  {
                    step: 3,
                    title: 'Implémentation Progressive',
                    description: 'Mise en œuvre des mesures de sécurité selon une approche priorisée, en commençant par les éléments les plus critiques pour votre organisation.',
                    icon: Shield,
                    circleBg: 'bg-indigo-500/20',
                    circleBorder: 'border-indigo-500',
                    circleText: 'text-indigo-400',
                    lineGradient: 'from-indigo-500',
                    cardBg: 'from-indigo-500/10 to-indigo-600/10',
                    cardBorder: 'border-indigo-500/30',
                    iconText: 'text-indigo-400'
                  },
                  {
                    step: 4,
                    title: 'Documentation Professionnelle',
                    description: 'Documentation complète de tous les processus, contrôles, et procédures selon les standards professionnels et les exigences de conformité.',
                    icon: CheckCircle,
                    circleBg: 'bg-blue-500/20',
                    circleBorder: 'border-blue-500',
                    circleText: 'text-blue-400',
                    lineGradient: 'from-blue-500',
                    cardBg: 'from-blue-500/10 to-blue-600/10',
                    cardBorder: 'border-blue-500/30',
                    iconText: 'text-blue-400'
                  },
                  {
                    step: 5,
                    title: 'Suivi et Amélioration Continue',
                    description: 'Surveillance continue de votre posture cybersécurité avec CyberOps, amélioration continue basée sur les métriques et les évolutions.',
                    icon: ArrowRight,
                    circleBg: 'bg-cyan-500/20',
                    circleBorder: 'border-cyan-500',
                    circleText: 'text-cyan-400',
                    lineGradient: 'from-cyan-500',
                    cardBg: 'from-cyan-500/10 to-cyan-600/10',
                    cardBorder: 'border-cyan-500/30',
                    iconText: 'text-cyan-400'
                  },
                ].map((step) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.step} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-12 h-12 rounded-full ${step.circleBg} border-2 ${step.circleBorder} flex items-center justify-center flex-shrink-0`}>
                          <span className={`${step.circleText} font-bold text-xl`}>{step.step}</span>
                        </div>
                        {step.step < 5 && (
                          <div className={`w-0.5 h-full bg-gradient-to-b ${step.lineGradient} to-purple-500 my-2`}></div>
                        )}
                      </div>
                      <div className={`bg-gradient-to-br ${step.cardBg} rounded-xl p-6 border ${step.cardBorder} flex-1`}>
                        <div className="flex items-center gap-3 mb-3">
                          <Icon className={`w-7 h-7 ${step.iconText}`} />
                          <h4 className="text-xl font-bold text-white">{step.title}</h4>
                        </div>
                        <p className="text-gray-300">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-purple-500/50">
                <h4 className="text-xl font-bold text-white mb-3 text-center">
                  Une Approche Rassurante et Professionnelle
                </h4>
                <p className="text-gray-300 text-center">
                  Une bonne prise en charge, c'est rassurant pour le client. Il voit qu'on comprend ses enjeux, 
                  qu'on a un plan, qu'on va l'accompagner. C'est la base de la confiance et de l'excellence BZ Inc.
                </p>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-purple-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 font-semibold shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

